/** @jsxImportSource @emotion/react */
import { Text, View } from "@react-pdf/renderer";
import { getFilledArrayOrDefault, isFilledArray } from "../../../../../../utils";
import { PDFPageTemplate } from "../../../../Tenants/Forms/PDFDynamicSectionsConfigForm";
import { PDFDefectGroups, PDFSingleColumnMeta, PDFTwoColumnMetaContent, PDFTwoColumnMetaLayout } from "../components";
import { PDFCheckFruitTables } from "../PDFCheckFruitTables";
import PDFDryMatterMinMaxAvgOverview from "../PDFDryMatterMinMaxAvgOverview";
import { PDFImages } from "../PDFImages";
import { PDFDocument, PDFPage } from "../PDFLayout";
import { PDFPallets } from "../PDFPallets";
import PDFPalletScoringOverview from "../PDFPalletScoringOverview";
import { PalletAnchors, renderWithPalletAnchors } from "../utils/pdfPalletLinks";
import { filterRenderUnitsByReportView } from "../utils/reportView";

export const PDFDynamicSectionsReport = ({
    onRender,
    pdf_config,
    layer,
    dynamicSectionsData,
    config,
    viewType = "full",
}) => {
    const renderSection = (block, index, fruitType: string | undefined, anchors: PalletAnchors) => {
        const data = dynamicSectionsData?.[index];
        if (!data) return [];

        const logo_path = data?.logo_base64 || "";
        const header_title = block.title || pdf_config?.title || "";

        const getSectionDefectGroups = (targetLayer = layer, check: any = undefined) => config.applyDisplayFilterFields(block?.defect_groups, {
            layer_type: targetLayer?.type || layer.type,
            fruit_type: targetLayer?.fruit_type || layer.fruit_type,
            layer: targetLayer || layer,
            check,
        });


        const getFields = (layer, check, callSetValue = true, childrenContext = data?.children) => {
            let primaryFields = config.applyDisplayFilterLayerFields(getFilledArrayOrDefault(block.primary_fields), { layer_type: layer.type, fruit_type: layer.fruit_type, layer });
            if (callSetValue) {
                primaryFields = primaryFields.map((field) => field.clone().set_value({
                    config,
                    layer,
                    check,
                    children: childrenContext,
                    form_options: config.form_options
                }));
            }

            let secondaryFields = config.applyDisplayFilterLayerFields(getFilledArrayOrDefault(block.secondary_fields), { layer_type: layer.type, fruit_type: layer.fruit_type, layer });
            if (callSetValue) {
                secondaryFields = secondaryFields.map((field) => field.clone().set_value({
                    config,
                    layer,
                    check,
                    children: childrenContext,
                    form_options: config.form_options
                }));
            }

            return { primaryFields, secondaryFields };
        };

        const scopedUnits = filterRenderUnitsByReportView(
            getFilledArrayOrDefault(data?.renderUnits),
            getFilledArrayOrDefault(data?.children),
            viewType,
            layer?.id
        ).filter((unit) => !fruitType || !unit?.fruit_type || unit?.fruit_type === fruitType);

        const scopedChildren = fruitType
            ? getFilledArrayOrDefault(data?.children).filter((child) => child?.fruit_type === fruitType)
            : getFilledArrayOrDefault(data?.children);


        switch (block.page_template) {
        case PDFPageTemplate.two_col_layer_meta:
        {
            // One page per render unit. The units (which layers, and whether checks are merged /
            // split per day / per check / per fruit_type) are determined by the section's
            // merge_strategy in useDynamicSectionsData.
            return scopedUnits.flatMap((unit) => {
                const { primaryFields, secondaryFields } = getFields(unit.layer, unit.latestCheck, true, unit.children);
                const defectGroups = data?.showDefects ? getSectionDefectGroups(unit.layer, unit.latestCheck) : [];
                const hasDefectGroups = isFilledArray(defectGroups);
                const hasTwoColContent = isFilledArray(primaryFields) || isFilledArray(secondaryFields) || hasDefectGroups;
                const unitKey = `${index}-${fruitType || "all"}-${unit.key}`;

                const anchorId = hasTwoColContent ? anchors.claim(unit.layer) : undefined;

                return [
                    (hasTwoColContent && <PDFPage key={unitKey} logo_path={logo_path} header_title={header_title} anchorId={anchorId}>
                        <PDFTwoColumnMetaLayout
                            layer_fields={primaryFields}
                            pdf_config={pdf_config}
                            general_status={unit.generalStatus}
                        >
                            {hasDefectGroups && (
                                <PDFDefectGroups
                                    allDefects={data?.defectDefinitions}
                                    defectGroups={defectGroups}
                                    checks={unit.checks}
                                />
                            )}

                            {isFilledArray(secondaryFields) && (
                                <PDFTwoColumnMetaContent
                                    fields={secondaryFields}
                                />
                            )}

                            {unit.dryMatterMinMax && (
                                <PDFDryMatterMinMaxAvgOverview dryMatterMinMaxAvg={unit.dryMatterMinMax} />
                            )}

                            {unit.scoringOverview && (
                                <PDFPalletScoringOverview {...unit.scoringOverview} />
                            )}
                        </PDFTwoColumnMetaLayout>
                    </PDFPage>),
                    // Explicitly show images from the unit's checks
                    (data.showImages ? getFilledArrayOrDefault(unit.checks) : []).map((check, idx) => <PDFImages key={`${unitKey}-img-${idx}`} images={check?.all_images} />)
                ];
            });
        }

        case PDFPageTemplate.pallets_table: {
            const childrenByFruitType = scopedChildren.reduce((acc, child) => {
                if (!child.fruit_type) return acc;

                acc[child.fruit_type] = [...(acc[child.fruit_type] || []), child];
                return acc;
            }, {});
            return Object.entries(childrenByFruitType)?.map(([fruit_type, items]) => (
                <PDFPallets
                    key={`${index}-${fruit_type}`}
                    fields={getFields({ ...layer, fruit_type }, data?.latestCheck, false).primaryFields}
                    printChildren={items as any[]}
                    config={config}
                    getPalletLinkTarget={block.link_pallet_labels ? anchors.linkTarget : undefined}
                />
            ));
        }

        case PDFPageTemplate.one_col_layer_meta:
            return scopedUnits.map((unit) => (
                <PDFPage key={`${index}-${fruitType || "all"}-${unit.key}`} logo_path={logo_path} header_title={header_title} anchorId={anchors.claim(unit.layer)}>
                    <PDFSingleColumnMeta fields={getFields(unit.layer, unit.latestCheck, true, unit.children).primaryFields} title={block.title} />
                </PDFPage>
            ));

        case PDFPageTemplate.check_summary_fruit_table:
            return scopedUnits.flatMap((unit) => {
                const check = unit.latestCheck;
                if (!check?.table_data) return [];
                // PDFCheckFruitTables renders no page without table headers.
                const anchorId = isFilledArray(check.table_data[0]) ? anchors.claim(unit.layer) : undefined;
                const unitKey = `${index}-${fruitType || "all"}-${unit.key}`;
                return [
                    <PDFCheckFruitTables
                        key={`${unitKey}-table`}
                        fields={getFields(check.layer, check, true, unit.children).primaryFields}
                        header={check.table_data[0]}
                        rows={check.table_data[1]}
                        footer={check.table_data[2]}
                        tenantDefectDefinitions={data?.defectDefinitions}
                        defect_groups={getSectionDefectGroups(check?.layer || layer, check)}
                        check={check}
                        logo_path={logo_path}
                        header_title={header_title}
                        anchorId={anchorId}
                    />,
                    <PDFImages key={`${unitKey}-img`} images={check.all_images} />
                ];
            });

        default:
            return (
                <PDFPage key={`${index}-${fruitType || "all"}`} logo_path={logo_path} header_title={header_title}>
                    <View>
                        <Text>Unknown block type: {block.page_template}</Text>
                    </View>
                </PDFPage>
            );
        }
    };

    const allFruitTypes = Array.from(new Set(
        getFilledArrayOrDefault(dynamicSectionsData)
            .flatMap((section) => [
                ...getFilledArrayOrDefault(section?.fruitTypes),
                ...getFilledArrayOrDefault(section?.renderUnits).map((unit) => unit?.fruit_type),
                ...getFilledArrayOrDefault(section?.children).map((child) => child?.fruit_type),
            ])
            .filter((fruitType) => !!fruitType && fruitType !== "mixed")
    ));

    const linkPalletLabels = getFilledArrayOrDefault(pdf_config?.dynamic_sections)
        .some((block) => block?.page_template === PDFPageTemplate.pallets_table && block?.link_pallet_labels);

    const pdfContent = renderWithPalletAnchors(linkPalletLabels, layer?.id, (anchors) => (allFruitTypes.length > 0
        ? allFruitTypes.flatMap((fruitType) => (pdf_config?.dynamic_sections || [])
            .flatMap((block, index) => renderSection(block, index, fruitType, anchors)))
        : (pdf_config?.dynamic_sections || []).flatMap((block, index) => renderSection(block, index, undefined, anchors))));

    return (
        <PDFDocument onRender={onRender} title={`${pdf_config?.title} ${layer?.label}`}>
            {pdfContent}
        </PDFDocument>
    );
};
